import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getOrderByCode, getOrdersMe } from "apis/orderApi";
import { getResnapOrders } from "apis/resnapApi";
import { mapToObject, mapToPages } from "common/mergeInNewPage";
import { RequestState } from "common/RequestState";
import subDays from "date-fns/subDays";
import { InternalOrderStatus, ORDERS_PAGE_SIZE } from "./orderConstants";

export const requestOrderByCode = createAsyncThunk("orders/requestByCode", async code => {
  const isResnapOrder = /^\d+/.test(code); // Resnap order codes start with a digit, Albelli order codes start with a letter
  let order = isResnapOrder ? (await getResnapOrders()).find(o => o.code === code) : await getOrderByCode(code);

  if (!order) throw new Error("Order not found");

  return order;
});

/**
 * Fetches orders from both Albelli and Resnap.
 * @param {boolean} all - Loads all orders if true; loads first 100 albelli orders and all resnap orders if false
 */
export const requestOrderList = createAsyncThunk("orders/requestList", async (all = false) => {
  const albelliOrders = all ? await getAllAlbelliOrders() : await getOrdersMe();
  const resnapOrders = await getResnapOrders();
  const mergedOrdersList = [...resnapOrders, ...albelliOrders.items];

  const historyDate = subDays(new Date(), 30); // orders made more than 30 days ago should become history
  const historyPaidDate = subDays(new Date(), 14); // order paid more than 14 days ago should become history
  const byCreatedDate = (a, b) => (new Date(a.createdAt) < new Date(b.createdAt) ? 1 : -1);

  const inProgressOrders = mergedOrdersList
    .filter(
      o =>
        new Date(o.createdAt) > historyDate &&
        o.products.every(
          p =>
            p.status !== InternalOrderStatus.Dispatched ||
            !p.paymentChangedDate || // Resnap orders don't have paymentChangedDate
            new Date(p.paymentChangedDate) > historyPaidDate
        )
    )
    .sort(byCreatedDate);

  const historyOrders = mergedOrdersList.filter(o => !inProgressOrders.includes(o)).sort(byCreatedDate);

  return {
    items: mapToObject(mergedOrdersList, o => o.code),
    inProgress: {
      pages: mapToPages(inProgressOrders, o => o.code, ORDERS_PAGE_SIZE),
      pageSize: ORDERS_PAGE_SIZE,
      total: inProgressOrders.length
    },
    history: {
      pages: mapToPages(historyOrders, o => o.code, ORDERS_PAGE_SIZE),
      pageSize: ORDERS_PAGE_SIZE,
      total: albelliOrders.total + resnapOrders.length - inProgressOrders.length
    }
  };
});

/**
 * Loads all user's albelli orders. OrderAPI can return max 100 orders per request
 */
const getAllAlbelliOrders = async () => {
  let items = [];
  let total;

  while (total === undefined || items.length < total) {
    const newBatch = await getOrdersMe(items.length, 100);
    total = newBatch.total;
    items = items.concat(newBatch.items);
  }

  return { items, total };
};

export const ordersSlice = createSlice({
  name: "orders",
  initialState: {
    _requestState: RequestState.NotRequested,
    inProgress: {},
    history: {},
    items: {}
  },
  extraReducers: {
    [requestOrderByCode.pending]: (state, action) => {
      state.items[action.meta.arg] = {
        ...state.items[action.meta.arg],
        _requestState: RequestState.InProgress
      };
    },
    [requestOrderByCode.fulfilled]: (state, action) => {
      state.items[action.meta.arg] = {
        ...state.items[action.meta.arg],
        ...action.payload,
        _requestState: RequestState.Finished
      };
    },
    [requestOrderByCode.rejected]: (state, action) => {
      state.items[action.meta.arg] = {
        ...state.items[action.meta.arg],
        _requestState: RequestState.Failed
      };
    },
    [requestOrderList.pending]: state => {
      state._requestState = RequestState.InProgress;
    },
    [requestOrderList.fulfilled]: (state, action) => {
      state._requestState = RequestState.Finished;
      state.items = action.payload.items;
      state.inProgress = action.payload.inProgress;
      state.history = action.payload.history;
    },
    [requestOrderList.rejected]: state => {
      state._requestState = RequestState.Failed;
    }
  }
});
